import React from "react";
import { ExternalLink } from "components/ExternalLink";
import {
  CodeBlock,
  InlineCode,
  CopyableCodeLine,
} from "onboarding/components/NumberedStep";
import { track } from "utils/track";
import {
  APPOPTICS_INSTALLER_URL_BASE,
  KB_URL_BASE,
} from "onboarding/constants";

const SCRIPT = "appoptics-java.sh";
const WIZARD = "JavaAgent_Setup.exe";
const SCRIPT_URL = `${APPOPTICS_INSTALLER_URL_BASE}/${SCRIPT}`;
const WIZARD_URL = `${APPOPTICS_INSTALLER_URL_BASE}/java/latest/${WIZARD}`;

const KB_URL = `${KB_URL_BASE}/apm_tracing/java/install/`;
const INSTALL_AGENT_LINUX_URL = `${KB_URL}#getting-the-agent-jar`;
const INSTALL_AGENT_WINDOWS_URL = `${KB_URL}#windows`;
const ENABLE_AGENT_LINUX_URL = `${KB_URL}#enabling-the-agent`;
const ENABLE_AGENT_WINDOWS_URL = `${KB_URL}#enabling-the-agent-on-windows`;

const javaInstructions = (props, type) => {
  const { agentRecorderToken, serviceName, selectedOperatingSystem } = props;
  switch (selectedOperatingSystem) {
    case "linux":
      return [
        {
          precedingContent: (
            <div onClick={_ => track("Clicked the Java APM docs link")}>
              The install script below will manage downloading and configuring
              the agent. If you’d prefer to do this manually or need more help,{" "}
              <ExternalLink href={INSTALL_AGENT_LINUX_URL}>
                check out our documentation
              </ExternalLink>.
            </div>
          ),
          title:
            "Navigate to the desired installation directory and download the install script:",
          content: (
            <div onClick={_ => track("Copied the Java APM downloader script")}>
              <CopyableCodeLine shell>curl -sSO {SCRIPT_URL}</CopyableCodeLine>
            </div>
          ),
        },
        {
          title:
            "Execute the script to download the Java agent to the current directory:",
          content: (
            <div onClick={_ => track("Copied the Java APM installer script")}>
              <CopyableCodeLine shell>
                sh {SCRIPT} --service-key={agentRecorderToken}:{serviceName}
              </CopyableCodeLine>
            </div>
          ),
        },
        {
          title: (
            <span>
              Configure the JVM to load the agent by setting the{" "}
              <InlineCode>-javaagent</InlineCode> parameter. That will look like
              this:
            </span>
          ),
          content: (
            <div>
              <div
                onClick={_ => track("Copied the Java APM configuration script")}
              >
                <CodeBlock>
                  -javaagent:&lt;agent installation path&gt;/appoptics-agent.jar
                </CodeBlock>
              </div>
              <p>
                <ExternalLink href={ENABLE_AGENT_LINUX_URL}>
                  The docs have a quick guide on where to set the{" "}
                  <InlineCode>-javaagent</InlineCode> flag for all supported web
                  servers.
                </ExternalLink>
              </p>
            </div>
          ),
        },
        {
          title:
            "Restart the JVM to load the agent. In a few seconds, the agent will be connected to AppOptics.",
        },
      ];
    case "windows":
      return [
        {
          precedingContent: (
            <div>
              The installer will manage installing and configuring the agent.
              For more information on how it works and automating install,{" "}
              <ExternalLink href={INSTALL_AGENT_WINDOWS_URL}>
                check out our documentation
              </ExternalLink>.
            </div>
          ),
          title: "Download the Java agent installation wizard:",
          content: (
            <p onClick={_ => track("Clicked the Windows Java APM wizard link")}>
              <a href={WIZARD_URL}>{WIZARD_URL}</a>
            </p>
          ),
        },
        {
          title:
            "Run the installation wizard and follow the steps, providing this service key when requested:",
          content: (
            <div
              onClick={_ => track("Copied the Windows Java APM token:service")}
            >
              <CopyableCodeLine>
                {agentRecorderToken}:{serviceName}
              </CopyableCodeLine>
            </div>
          ),
        },
        {
          title: `Configure the ${type} webapp to use the agent:`,
          content: (
            <p
              onClick={_ =>
                track("Clicked the Windows Java APM enable agent windows link")}
            >
              <ExternalLink href={ENABLE_AGENT_WINDOWS_URL}>
                {ENABLE_AGENT_WINDOWS_URL}
              </ExternalLink>
            </p>
          ),
        },
        {
          title:
            "Restart the JVM to load the agent. In a few seconds, the agent will be connected to AppOptics.",
        },
      ];
    default:
      return null;
  }
};

export default type => props => javaInstructions(props, type);
